<template>
  <div class="d-inline-block">
    <!-- Botón principal con icono de sincronización -->
    <button 
      class="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
      @click="openModal"
      :title="$t('calendarSync.syncWithGoogleCalendar')"
    >
      <i class="fab fa-google calendar-sync-icon"></i>
    </button>

    <!-- Modal de sincronización usando Bootstrap -->
    <div 
      v-if="showModal" 
      class="modal d-block" 
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5);"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header bg-primary text-white">
            <div class="d-flex align-items-center gap-2">
              <i class="fab fa-google fs-4"></i>
              <h5 class="modal-title mb-0">{{ $t('calendarSync.calendarSynchronization') }}</h5>
            </div>
            <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- Estado de carga -->
            <ContentSkeleton v-if="loading" variant="form" :rows="4" padded />

            <!-- Contenido principal -->
            <div v-else>
              <!-- Descripción -->
              <div class="alert alert-info d-flex align-items-start gap-2 mb-3">
                <i class="material-icons">info</i>
                <span>{{ $t('calendarSync.syncDescription') }}</span>
              </div>

              <!-- Nota sobre el período de eventos -->
              <div class="alert alert-secondary d-flex align-items-start gap-2 mb-4">
                <i class="material-icons">date_range</i>
                <small>{{ $t('calendarSync.eventsTimeframeNote') }}</small>
              </div>

              <!-- URL del Feed -->
              <div class="mb-4">
                <label class="form-label fw-bold d-flex align-items-center gap-2">
                  <i class="material-icons">link</i>
                  {{ $t('calendarSync.yourCalendarFeedUrl') }}
                </label>
                <div class="input-group">
                  <input 
                    type="text" 
                    class="form-control font-monospace small bg-light" 
                    :value="feedUrl" 
                    readonly
                    ref="urlInput"
                  />
                  <button 
                    class="btn btn-primary"
                    @click="copyUrl"
                    :title="$t('calendarSync.copyUrl')"
                  >
                    <i class="material-icons">{{ copied ? 'check' : 'content_copy' }}</i>
                  </button>
                </div>
                <small v-if="copied" class="text-success d-flex align-items-center gap-1 mt-2">
                  <i class="material-icons" style="font-size: 16px;">check_circle</i>
                  {{ $t('calendarSync.urlCopied') }}
                </small>
              </div>

              <!-- Estadísticas -->
              <div v-if="syncStatus.hasToken" class="row g-3 mb-4">
                <div class="col-auto">
                  <div class="d-flex align-items-center gap-2 text-muted small">
                    <i class="material-icons" style="font-size: 18px;">calendar_today</i>
                    <span>{{ $t('calendarSync.created') }}: {{ formatDate(syncStatus.createdAt) }}</span>
                  </div>
                </div>
                <div v-if="syncStatus.lastAccessed" class="col-auto">
                  <div class="d-flex align-items-center gap-2 text-muted small">
                    <i class="material-icons" style="font-size: 18px;">schedule</i>
                    <span>{{ $t('calendarSync.lastSync') }}: {{ formatDate(syncStatus.lastAccessed) }}</span>
                  </div>
                </div>
                <div class="col-auto">
                  <div class="d-flex align-items-center gap-2 text-muted small">
                    <i class="material-icons" style="font-size: 18px;">sync</i>
                    <span>{{ $t('calendarSync.syncCount') }}: {{ syncStatus.accessCount || 0 }}</span>
                  </div>
                </div>
              </div>

              <!-- Instrucciones desplegables (Accordion Bootstrap) -->
              <div class="accordion mb-4" id="instructionsAccordion">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button 
                      class="accordion-button" 
                      :class="{ collapsed: !showInstructions }"
                      type="button" 
                      @click="showInstructions = !showInstructions"
                    >
                      <i class="material-icons me-2">help_outline</i>
                      {{ $t('calendarSync.howToAddToGoogleCalendar') }}
                    </button>
                  </h2>
                  <div class="accordion-collapse collapse" :class="{ show: showInstructions }">
                    <div class="accordion-body">
                      <!-- Steps -->
                      <div class="mb-4">
                        <div v-for="(step, index) in instructionSteps" :key="index" class="d-flex gap-3 mb-3">
                          <div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0" style="width: 32px; height: 32px;">
                            {{ index + 1 }}
                          </div>
                          <div>
                            <strong class="d-block">{{ step.title }}</strong>
                            <p class="text-muted small mb-0" v-html="step.description"></p>
                          </div>
                        </div>
                      </div>

                      <!-- Nota sobre actualización -->
                      <div class="alert alert-warning d-flex gap-2 mb-3">
                        <i class="material-icons">info</i>
                        <div>
                          <strong>{{ $t('calendarSync.note') }}:</strong>
                          {{ $t('calendarSync.syncNote') }}
                        </div>
                      </div>

                      <!-- Otros calendarios -->
                      <div class="border-top pt-3">
                        <h6 class="fw-bold">{{ $t('calendarSync.otherCalendarApps') }}</h6>
                        <ul class="text-muted small mb-0">
                          <li class="mb-2"><strong>Outlook:</strong> {{ $t('calendarSync.outlookInstructions') }}</li>
                          <li class="mb-2"><strong>Apple Calendar:</strong> {{ $t('calendarSync.appleCalendarInstructions') }}</li>
                          <li><strong>Thunderbird:</strong> {{ $t('calendarSync.thunderbirdInstructions') }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Botón para regenerar token -->
              <div class="border-top pt-3">
                <button 
                  class="btn btn-outline-warning btn-sm d-flex align-items-center gap-2"
                  @click="confirmRegenerate"
                  :disabled="regenerating"
                >
                  <i class="material-icons">refresh</i>
                  {{ regenerating ? $t('calendarSync.regenerating') : $t('calendarSync.regenerateUrl') }}
                </button>
                <small class="text-muted d-block mt-2">
                  {{ $t('calendarSync.regenerateWarning') }}
                </small>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t('calendarSync.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from '@/api/wencesApi';
import Swal from 'sweetalert2';
import ContentSkeleton from '@/components/common/ContentSkeleton.vue';

export default {
  name: 'CalendarSync',
  components: { ContentSkeleton },
  data() {
    return {
      showModal: false,
      showInstructions: false,
      loading: false,
      regenerating: false,
      copied: false,
      feedUrl: '',
      syncStatus: {
        hasToken: false,
        isConfigured: false,
        createdAt: null,
        lastAccessed: null,
        accessCount: 0
      }
    };
  },
  computed: {
    instructionSteps() {
      return [
        {
          title: this.$t('calendarSync.copyTheUrl'),
          description: this.$t('calendarSync.copyUrlDescription')
        },
        {
          title: this.$t('calendarSync.openGoogleCalendar'),
          description: `${this.$t('calendarSync.goTo')} <a href="https://calendar.google.com" target="_blank" class="text-primary">calendar.google.com</a>`
        },
        {
          title: this.$t('calendarSync.addCalendarByUrl'),
          description: this.$t('calendarSync.addCalendarByUrlDescription')
        },
        {
          title: this.$t('calendarSync.pasteTheUrl'),
          description: this.$t('calendarSync.pasteUrlDescription')
        },
        {
          title: this.$t('calendarSync.done'),
          description: this.$t('calendarSync.doneDescription')
        }
      ];
    }
  },
  methods: {
    async openModal() {
      this.showModal = true;
      await this.loadSyncData();
    },
    
    closeModal() {
      this.showModal = false;
      this.showInstructions = false;
    },
    
    async loadSyncData() {
      this.loading = true;
      try {
        const { data } = await wencesApi.get('/api/calendar-sync/token');
        if (data.ok) {
          this.feedUrl = data.data.feedUrl;
          this.syncStatus = {
            hasToken: true,
            isConfigured: true,
            createdAt: data.data.createdAt,
            lastAccessed: data.data.lastAccessed,
            accessCount: data.data.accessCount
          };
        }
      } catch (error) {
        console.error('Error loading calendar sync data:', error);
        this.$notify({
          type: 'danger',
          message: this.$tn('calendarSync.errorLoadingConfig')
        });
      } finally {
        this.loading = false;
      }
    },
    
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.feedUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 3000);
      } catch (error) {
        const input = this.$refs.urlInput;
        input.select();
        document.execCommand('copy');
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 3000);
      }
    },
    
    async confirmRegenerate() {
      const result = await Swal.fire({
        title: this.$tn('calendarSync.regenerateUrlConfirm'),
        text: this.$tn('calendarSync.regenerateUrlWarning'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f0ad4e',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$tn('calendarSync.yesRegenerate'),
        cancelButtonText: this.$tn('Cancel')
      });
      
      if (result.isConfirmed) {
        await this.regenerateToken();
      }
    },
    
    async regenerateToken() {
      this.regenerating = true;
      try {
        const { data } = await wencesApi.post('/api/calendar-sync/regenerate-token');
        if (data.ok) {
          this.feedUrl = data.data.feedUrl;
          this.syncStatus.createdAt = data.data.createdAt;
          this.syncStatus.lastAccessed = null;
          this.syncStatus.accessCount = 0;
          
          this.$notify({
            type: 'success',
            message: this.$tn('calendarSync.urlRegenerated')
          });
        }
      } catch (error) {
        console.error('Error regenerating token:', error);
        this.$notify({
          type: 'danger',
          message: this.$tn('calendarSync.errorRegeneratingUrl')
        });
      } finally {
        this.regenerating = false;
      }
    },
    
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString(
        this.$i18n?.locale === 'es' ? 'es-ES' : 'en-US',
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
      );
    }
  }
};
</script>

<style scoped>
/*
 * Mismo tamaño que los botones vecinos del dashboard: ancho del material-icon (17px) y
 * alto de la línea del .btn-sm (21px), que este botón flex no toma solo.
 */
.calendar-sync-icon {
  width: 17px;
  height: 21px;
  line-height: 21px;
  font-size: 15px;
  text-align: center;
}

.font-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.material-icons {
  font-size: 20px;
  vertical-align: middle;
}
</style>
